import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Copy, Check, Share2, Coins } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { fmt } from "@/components/NotificationBell";

export function inviteLink(username) {
  return `${window.location.origin}/api/invito/${encodeURIComponent(username || "")}`;
}

export async function shareInvite(username, t, onCopied) {
  const url = inviteLink(username);
  const text = `${t("home_share_text")} ${url}`;
  try {
    if (navigator.share) { await navigator.share({ title: "Deuro", text: t("home_share_text"), url }); return; }
    await navigator.clipboard.writeText(text);
    onCopied?.();
  } catch { /* cancelled */ }
}

export default function ReferralCard({ compact = false }) {
  const { user } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
  }, [user?.referrals_count]);

  const link = inviteLink(user?.username);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border border-[#e5dfd3] card-shadow bg-white ${compact ? "p-5" : "p-6"}`} data-testid="referral-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-[#059669]" />
        <h2 className="font-head font-bold text-lg">{t("ref_title")}</h2>
      </div>
      <p className="text-sm text-[#5f6b62]">{fmt(t("ref_sub"), { amount: data?.bonus_per_invite ?? 25 })}</p>

      <div className="mt-4 flex gap-2">
        <input readOnly value={link} data-testid="referral-link" onFocus={(e) => e.target.select()} className="deuro-input py-2.5 font-mono text-xs" />
        <button onClick={copy} data-testid="referral-copy" className="px-3 rounded-xl border-2 border-[#e5dfd3] hover:border-[#1e3326] shrink-0" title={t("ref_copy")}>
          {copied ? <Check className="w-4 h-4 text-[#059669]" /> : <Copy className="w-4 h-4" />}
        </button>
        <button onClick={() => shareInvite(user?.username, t, () => { setCopied(true); setTimeout(() => setCopied(false), 2000); })} data-testid="referral-share"
          className="flex items-center gap-1.5 px-4 rounded-xl bg-[#d97706] text-white text-sm font-semibold hover:bg-[#c26806] shrink-0">
          <Share2 className="w-4 h-4" /> {t("home_grow_cta")}
        </button>
      </div>
      {copied && <p className="text-xs text-[#059669] mt-1.5" data-testid="referral-copied">{t("ref_copied")}</p>}

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-xl bg-[#f4f0e8] p-3">
          <p className="text-xs text-[#5f6b62]">{t("ref_count")}</p>
          <p className="font-head font-extrabold text-2xl" data-testid="referral-count">{data?.count ?? user?.referrals_count ?? 0}</p>
          {data?.pending > 0 && <p className="text-[11px] text-[#92600a]">+{data.pending} {t("ref_pending")}</p>}
        </div>
        <div className="rounded-xl bg-[#fef3c7] p-3">
          <p className="text-xs text-[#92600a]">{t("ref_earned")}</p>
          <p className="font-mono font-extrabold text-2xl text-[#92600a] flex items-center gap-1" data-testid="referral-earned"><Coins className="w-5 h-5" /> {(data?.earned ?? user?.referral_earned ?? 0).toLocaleString("it-IT")}</p>
        </div>
      </div>
    </motion.div>
  );
}
